import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { getFromStorage, saveToStorage, deleteAllFromStorage } from "@/taskpane/lib";
import { AuthData } from "@/types/auth";
import { Customer } from "@/types/customer";
import { AnalyticsKey } from "@/types/analytics";
import { trackEvent } from "@/services/analytics-service";
import { getCustomerByName } from "@/services/customer-service";
import { api, loginOktaUser, loginUser, logoutUser, refreshToken } from "@/services/auth-service";
import posthog from "posthog-js";
import { riskAnalysisCancelTokens, resetRiskAnalysisCanceledTokens } from "@/services/risk-analysis-service";
import { useOkta } from "./okta";
import { useOktaAuth } from "@okta/okta-react";
import { ADDIN_BASE_URL } from "@/constants";
import log from "../utils/logger";

interface AuthContextType {
  isLoading: boolean;
  isLoadingLogin: boolean;
  isAuthenticated: boolean;
  user: AuthData | null;
  customer: Customer | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  loginWithOkta: () => Promise<void>;
  logoutWithOkta: () => Promise<void>;
  loadCustomer: (name: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { oktaAuth } = useOktaAuth();
  const { resetOktaConfig, setOktaConfig } = useOkta();

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLogin, setIsLoadingLogin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [user, setUser] = useState<AuthData | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);

  const loadCustomer = async (name: string) => {
    try {
      const customer = await getCustomerByName(name);
      if (!customer) throw Error("Organization not found!");
      if (!customer.oktaIssuerUrl || !customer.oktaClientId) {
        throw Error("Not a valid Okta organization!");
      }

      setCustomer(customer);
      setOktaConfig({
        issuer: customer.oktaIssuerUrl,
        clientId: customer.oktaClientId,
        scopes: ["openid", "profile", "email", "offline_access"],
        redirectUri: ADDIN_BASE_URL,
        pkce: true,
      });
    } catch (error) {
      log.error("Error loading customer", undefined, error);
      throw error;
    }
  };

  const login = async (username: string, password: string) => {
    setIsLoadingLogin(true);
    try {
      const authData = await loginUser(username, password);
      trackEvent(AnalyticsKey.AUTH.LOGIN.SUCCESS, {
        userId: authData.user.id,
        username,
      });
      setUser(authData);
      setIsAuthenticated(true);
      await saveToStorage("user", JSON.stringify(authData));
      resetRiskAnalysisCanceledTokens();
      api.defaults.headers.common["Authorization"] = `Bearer ${authData.accessToken}`;
    } catch (error) {
      log.error("Error logging in", undefined, error);
      trackEvent(AnalyticsKey.AUTH.LOGIN.FAILURE, {
        username,
        errorMessage: error?.message || "Error logging in",
      });
      throw error;
    } finally {
      setIsLoadingLogin(false);
    }
  };

  const loginWithOkta = async () => {
    setIsLoadingLogin(true);
    try {
      // Login with Okta credentials
      const res = await oktaAuth.token.getWithPopup({ popupTitle: "Log In with OKTA" });
      await oktaAuth.handleLoginRedirect(res.tokens);

      // Get Okta user and log them in on Yellowpad
      const oktaUser = await oktaAuth.getUser();
      const authData = await loginOktaUser(oktaUser.name, oktaUser.email, oktaUser.email, customer.id, res.tokens);
      trackEvent(AnalyticsKey.AUTH.LOGIN.SUCCESS, {
        userId: authData.user.id,
        username: authData.user.username,
      });

      // Save data to storage and set the auth header
      await saveToStorage("user", JSON.stringify(authData));
      resetRiskAnalysisCanceledTokens();
      api.defaults.headers.common["Authorization"] = `Bearer ${authData.accessToken}`;

      // Reload UI
      setUser(authData);
      setIsAuthenticated(true);
    } catch (error) {
      log.error("Error logging in with Okta", undefined, error);
      resetOktaConfig();
      throw error;
    } finally {
      setIsLoadingLogin(false);
    }
  };

  const logout = async () => {
    // Fire backend logout in background - don't await
    logoutUser().catch(error => {
      log.error("Error calling logout endpoint:", undefined, error);
    });
    
    // Immediately clear local state
    posthog.reset();
    setUser(null);
    setIsAuthenticated(false);
    await deleteAllFromStorage();
    delete api.defaults.headers.common["Authorization"];

    Object.values(riskAnalysisCancelTokens).forEach((token) => token.cancel());
  };

  const logoutWithOkta = async () => {
    try {
      // Fire backend logout in background
      logoutUser().catch(error => {
        log.error("Error calling logout endpoint:", undefined, error);
      });
      
      // Proceed with Okta logout
      await Promise.all([oktaAuth.closeSession(), oktaAuth.revokeAccessToken(), oktaAuth.revokeRefreshToken()]);
      resetOktaConfig();
    } catch (error) {
      log.error("Error logging out with Okta:", undefined, error);
      throw error;
    }
  };

  const refreshAuth = async () => {
    try {
      const storageUser = await getFromStorage("user");
      const currentUser = JSON.parse(storageUser || "{}");
      if (currentUser?.refreshToken) {
        const refreshedData = await refreshToken(currentUser.refreshToken);
        setUser(refreshedData);
        setIsAuthenticated(true);
        await saveToStorage("user", JSON.stringify(refreshedData));
        api.defaults.headers.common["Authorization"] = `Bearer ${refreshedData.accessToken}`;
      }
    } catch (error) {
      log.error("Error refreshing token:", undefined, error);
      await logout();
    }
  };

  const loadUser = async () => {
    setIsLoading(true);
    try {
      const storageUser = await getFromStorage("user");
      const parsedUser = JSON.parse(storageUser || "{}") as AuthData;
      if (parsedUser?.accessToken) {
        setUser(parsedUser);
        setIsAuthenticated(true);
        api.defaults.headers.common["Authorization"] = `Bearer ${parsedUser.accessToken}`;
      }
    } catch (error) {
      log.error("Error loading user:", undefined, error);
    } finally {
      setIsLoading(false);
    }
  };

  const init = async () => {
    if (!user?.accessToken) await loadUser();
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isLoadingLogin,
        isAuthenticated,
        user,
        customer,
        login,
        logout,
        refreshAuth,
        loginWithOkta,
        logoutWithOkta,
        loadCustomer,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
